import React, { useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

// User Profile routes
import UserSettingsLayout from '../components/UserSettingsLayout';
// Settings Routes
import EmailSettings from './settings/Email';
import SecuritySettings from './settings/Security';
import TwoStepVerification from './settings/TwoStepVerification';
import ApiTokens from './settings/ApiTokens';
import RecentDevices from './settings/RecentDevices';
import PrivacySettings from './settings/Privacy';
import AccountPreferencesSettings from '../components/AccountPreferencesSettings';
import ConnectedAppSettings from './settings/ConnectedApps';
import LinkPreferencesSettings from './settings/LinkPreferences';
import CloseAccountSettings from './settings/CloseAccount';
import ProfileAndVisibilitySettings from './settings/ProfileAndVisibility';
import ProductsSettings from './settings/Products';

const getLocationHashAndSearch = ({
  hash,
  search,
}: Pick<Location, 'hash' | 'search'>) => `${hash}${search}`;

const PATH_ROOT = '/manage-profile';

const RedirectUnknownSettings = () => <Navigate to={PATH_ROOT} />;

const RootSettingsRedirect = () => {
  const location = useLocation();
  const hashAndSearch = getLocationHashAndSearch(location);

  return (
    <Navigate to={`${PATH_ROOT}/profile-and-visibility${hashAndSearch}`} />
  );
};

export const useScrollTopOnRouteChange = () => {
  const location = useLocation();
  const previousPathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== previousPathnameRef.current) {
      window.scrollTo(0, 0);
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);
};

export const ManageProfileRoutes = () => {
  useScrollTopOnRouteChange();

  return (
    <UserSettingsLayout>
      <Routes>
        <Route path={PATH_ROOT} element={<RootSettingsRedirect />} />
        <Route
          path={`${PATH_ROOT}/profile-and-visibility`}
          element={<ProfileAndVisibilitySettings />}
        />
        <Route path={`${PATH_ROOT}/email`} element={<EmailSettings />} />
        <Route
          path={`${PATH_ROOT}/security/two-step-verification`}
          element={<TwoStepVerification />}
        />
        <Route
          path={`${PATH_ROOT}/security/api-tokens`}
          element={<ApiTokens />}
        />
        <Route
          path={`${PATH_ROOT}/security/recent-devices`}
          element={<RecentDevices />}
        />
        <Route path={`${PATH_ROOT}/security`} element={<SecuritySettings />} />
        <Route path={`${PATH_ROOT}/privacy`} element={<PrivacySettings />} />
        <Route
          path={`${PATH_ROOT}/account-preferences`}
          element={<AccountPreferencesSettings />}
        />
        <Route path={`${PATH_ROOT}/apps`} element={<ConnectedAppSettings />} />
        <Route
          path={`${PATH_ROOT}/link-preferences`}
          element={<LinkPreferencesSettings />}
        />
        <Route path={`${PATH_ROOT}/products`} element={<ProductsSettings />} />
        <Route
          path={`${PATH_ROOT}/close-account`}
          element={<CloseAccountSettings />}
        />
        <Route path={`${PATH_ROOT}/*`} element={<RedirectUnknownSettings />} />
      </Routes>
    </UserSettingsLayout>
  );
};

export default ManageProfileRoutes;
